<template>
  <CardActionContent :title="$t('global.advanced-auto-scaling')" :buttons="getButtons()">
    <template #description>
      <div>
        <Alert
          v-if="disabled"
          :message="$t('global.advanced-auto-scaling-disabled-message')"
          variant="info"
          :dismissible="false"
        />
        <p>{{ $t("global.advanced-auto-scaling-description-1") }}</p>
        <p>
          <span>{{ $t("global.advanced-auto-scaling-description-2") }}</span>
          <strong>{{ $t("global.advanced-auto-scaling-description-3") }}</strong>
          <a :href="KB_LINK" target="_blank" class="ml-1">{{ $t("global.learn-more") }}</a>
        </p>
        <div v-if="isUpdating()">
          <LoadingState :size="50" />
        </div>
        <ErrorState v-else-if="isErrored()" />
        <AdvancedAutoScaleUsage
          v-else-if="advancedAutoScale && isEnabled()"
          :advanced-auto-scale="advancedAutoScale"
        />
      </div>
    </template>
  </CardActionContent>
</template>

<script>
import { computed, onMounted } from "@vue/composition-api";
import AdvancedAutoScaleUsage from "@/components/smart/cards/AdvancedAutoScaleUsage";
import Alert from "@/components/display/Alert";
import CardActionContent from "@/components/display/card-sub-components/CardActionContent";
import ErrorState from "@/components/display/ItemErrorState";
import LoadingState from "@/components/display/LoadingState";
import NavLink from "@/classes/NavLink";
import SiteDisableAdvancedAutoScale from "@/components/smart/modals/SiteDisableAdvancedAutoScale";
import SiteEnableAdvancedAutoScale from "@/components/smart/modals/SiteEnableAdvancedAutoScale";
import SiteUpdateAdvancedAutoScaleContainers from "@/components/smart/modals/SiteUpdateAdvancedAutoScaleContainers";
import store from "@/store";

export default {
  name: "AdvancedAutoScaling",
  props: {
    plan: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    site: {
      type: Object,
      required: true,
    },
  },
  components: {
    ErrorState,
    LoadingState,
    AdvancedAutoScaleUsage,
    Alert,
    CardActionContent,
  },
  setup(props, { root }) {
    const advancedAutoScale = computed(() => store.getters["sites/getAdvancedAutoScale"]);
    const phpContainer = computed(() => store.getters["containers/getPhp"]);
    const status = computed(() => store.getters["sites/getAdvancedAutoScaleStatus"]);

    const isEnabled = () => phpContainer.value?.state === "stable";
    const isErrored = () => status.value.isErrored;
    const isUpdating = () =>
      ["creating", "destroying"].includes(phpContainer.value?.state) || status.value.isLoading;

    const getButtons = () => {
      if (!(status.value.isLoaded || status.value.isUpdating)) return null;
      if (isEnabled()) {
        return [
          new NavLink(
            root.$t("global.advanced-auto-scale-add-containers"),
            null,
            openUpdateForm,
            false,
            "primaryOutline",
            null,
            isUpdating() || props.disabled
          ),
          new NavLink(
            root.$t("global.turn-off"),
            null,
            openDisableForm,
            false,
            "primary",
            null,
            isUpdating() || props.disabled
          ),
        ];
      } else {
        return [
          new NavLink(
            root.$t("global.turn-on"),
            null,
            openEnableForm,
            false,
            "primary",
            null,
            isUpdating() || props.disabled
          ),
        ];
      }
    };

    const KB_LINK =
      "https://help.nexcess.net/client-portal/what-is-nexcess-cloud-auto-scaling?from_search=49181016";

    const openEnableForm = () =>
      store.dispatch("modals/open", {
        template: SiteEnableAdvancedAutoScale,
        data: { plan: props.plan, site: props.site },
      });

    const openDisableForm = () =>
      store.dispatch("modals/open", {
        template: SiteDisableAdvancedAutoScale,
        data: { advancedAutoScale: advancedAutoScale.value, plan: props.plan, site: props.site },
      });

    const openUpdateForm = () =>
      store.dispatch("modals/open", {
        template: SiteUpdateAdvancedAutoScaleContainers,
        data: { advancedAutoScale: advancedAutoScale.value, plan: props.plan, site: props.site },
      });

    onMounted(() => {
      store.dispatch("sites/getAdvancedAutoScale", props.site.id);
      store.dispatch("containers/getList", props.site.id);
    });

    return {
      advancedAutoScale,
      getButtons,
      isEnabled,
      isErrored,
      isUpdating,
      status,
      KB_LINK,
    };
  },
};
</script>
